import React from "react";
import {
  Box,
  Typography,
  Slider,
  Paper,
  Checkbox,
  FormGroup,
  FormControlLabel,
} from "@mui/material";
import { Formik } from "formik";
import { useAnswers, useSaveAnswers } from "apiRequests/useAnswers";
import StepNav from "@/components/StepNav";

const awardOptions = [
  "School Awards",
  "Local Organization",
  "State/Regional",
  "Organizations",
  "National Organizations",
];

const Panel: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
    <Typography variant="h6" fontWeight="600" gutterBottom>
      {title}
    </Typography>
    {children}
  </Paper>
);

export default function Awards() {
  const { data, refetch, isReady } = useAnswers();
  const saveAnswers = useSaveAnswers();

  return !isReady ? (
    <p>Loading...</p>
  ) : (
    <Formik
      initialValues={{
        leadership: data?.personalInformation?.leadership || 1,
        awards: data?.personalInformation?.awards || ([] as string[]),
      }}
      onSubmit={(values) => {
        // personalInformation is stored as a single object that the server
        // overwrites wholesale, so merge our fields into the existing data.
        return saveAnswers({
          personalInformation: {
            ...data?.personalInformation,
            leadership: values.leadership,
            awards: values.awards,
          },
        }).then((results) => {
          refetch();
          console.log("Answers saved", results);
        });
      }}
    >
      {({ values, setFieldValue, handleSubmit, submitForm }) => (
        <form onSubmit={handleSubmit}>
          <Box p={4}>
            {/* Leadership & Awards */}
            <Panel title="Leadership & Awards">
              {/* Leadership bar */}
              <Typography gutterBottom fontWeight="500">
                Leadership Qualities
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Leadership qualities are demonstrated in various ways. Have you
                won any awards or recognition at or outside school? Do you have a
                leadership position in any school or extracurricular activity or
                group? An example of the highest level of leadership quality
                would be an individual who has won multiple awards, is the
                captain of at least one sports team, and holds a leadership
                position within another school/extracurricular activity. Another
                example is someone who has demonstrated extraordinary civic or
                volunteer activities, going so far above and beyond others so as
                to be recognized for their efforts outside the school system.
              </Typography>
              <Slider
                name="leadership"
                value={values.leadership}
                onChange={(_, val) => setFieldValue("leadership", val)}
                min={0}
                max={5}
                step={1}
                marks
                valueLabelDisplay="auto"
              />

              {/* Awards */}
              <Box mt={4}>
                <Typography gutterBottom fontWeight="500">
                  Awards
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Select every level at which you've received awards or
                  recognition.
                </Typography>
                <FormGroup>
                  {awardOptions.map((option) => (
                    <FormControlLabel
                      key={option}
                      control={
                        <Checkbox
                          checked={values.awards.indexOf(option) > -1}
                          onChange={() => {
                            const next = values.awards.includes(option)
                              ? values.awards.filter(
                                  (v: string) => v !== option
                                )
                              : [...values.awards, option];
                            setFieldValue("awards", next);
                          }}
                        />
                      }
                      label={option}
                    />
                  ))}
                </FormGroup>
              </Box>
            </Panel>

            <StepNav
              onSave={async () => {
                await submitForm();
                return true;
              }}
            />
          </Box>
        </form>
      )}
    </Formik>
  );
}
